// LearningAnalytics.js
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { ActivityLineChart, TopicPieChart, CategoryPieChart } from '../Charts';

const LearningAnalytics = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [activityData, setActivityData] = useState([]);
  const [topicData, setTopicData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // 병렬로 모든 데이터 fetch
      const [statsRes, activityRes, topicsRes, categoriesRes] = await Promise.all([
        fetch('/api/student-analytics/summary', { headers }),
        fetch('/api/student-analytics/activity?days=14', { headers }),
        fetch('/api/student-analytics/topics', { headers }),
        fetch('/api/student-analytics/image-categories', { headers })
      ]);

      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data.summary);
      }

      if (activityRes.ok) {
        const data = await activityRes.json();
        setActivityData(data.data || []);
      }

      if (topicsRes.ok) {
        const data = await topicsRes.json();
        setTopicData(data.data || []);
      }

      if (categoriesRes.ok) {
        const data = await categoriesRes.json();
        setCategoryData(data.data || []);
      }

    } catch (err) {
      console.error('데이터 로딩 실패:', err);
      setError('네트워크 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const getGradeContent = () => {
    switch (user?.gradeLevel) {
      case 'elementary_low':
        return {
          title: '나의 학습 기록',
          description: '지금까지 얼마나 많이 배웠는지 확인해보세요!',
          levelEmoji: '🌱',
          progressColor: 'from-pink-400 to-purple-500'
        };
      case 'elementary_high':
        return {
          title: '학습 성과 분석',
          description: '나의 학습 진도와 성취를 확인해보세요!',
          levelEmoji: '🚀',
          progressColor: 'from-blue-400 to-indigo-500'
        };
      default:
        return {
          title: '학습 분석 대시보드',
          description: '상세한 학습 통계와 진도를 확인하세요.',
          levelEmoji: '🎯',
          progressColor: 'from-green-400 to-teal-500'
        };
    }
  };

  const content = getGradeContent();

  // 학습 레벨 계산
  const calculateLevel = () => {
    if (!stats) return { level: '새싹', score: 0, nextMilestone: '첫 대화 시작하기' };

    const totalActivity = (stats.totalMessages || 0) + (stats.totalImages || 0) * 2;

    if (totalActivity >= 500) return { level: '마스터', score: 100, nextMilestone: '최고 레벨 달성!' };
    if (totalActivity >= 200) return { level: '전문가', score: 80, nextMilestone: '500 활동으로 마스터 되기' };
    if (totalActivity >= 100) return { level: '숙련자', score: 60, nextMilestone: '200 활동으로 전문가 되기' };
    if (totalActivity >= 50) return { level: '도전자', score: 40, nextMilestone: '100 활동으로 숙련자 되기' };
    if (totalActivity >= 10) return { level: '탐험가', score: 20, nextMilestone: '50 활동으로 도전자 되기' };
    return { level: '새싹', score: Math.min(totalActivity * 2, 19), nextMilestone: '10 활동으로 탐험가 되기' };
  };

  const learningProgress = calculateLevel();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <div className="text-red-500 mb-4">{error}</div>
        <button
          onClick={fetchAllData}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
        >
          다시 시도
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* 헤더 */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-8"
      >
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          {content.title}
        </h1>
        <p className="text-gray-600">{content.description}</p>
      </motion.div>

      {/* 학습 레벨 카드 */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl shadow-lg p-6 mb-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-800">
            {content.levelEmoji} 학습 레벨
          </h2>
          <div className="text-2xl font-bold text-gray-700">
            {learningProgress.level}
          </div>
        </div>

        <div className="mb-4">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>진도</span>
            <span>{learningProgress.score}점</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className={`h-3 rounded-full bg-gradient-to-r ${content.progressColor}`}
              style={{ width: `${Math.min(100, learningProgress.score)}%` }}
            ></div>
          </div>
        </div>

        <div className="text-sm text-gray-600">
          다음 목표: {learningProgress.nextMilestone}
        </div>
      </motion.div>

      {/* 통계 카드들 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow-md p-4"
        >
          <div className="text-center">
            <div className="text-2xl mb-2">📚</div>
            <div className="text-2xl font-bold text-blue-600">
              {stats?.totalSessions || 0}
            </div>
            <div className="text-sm text-gray-600">학습 세션</div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg shadow-md p-4"
        >
          <div className="text-center">
            <div className="text-2xl mb-2">💬</div>
            <div className="text-2xl font-bold text-green-600">
              {stats?.totalMessages || 0}
            </div>
            <div className="text-sm text-gray-600">대화 횟수</div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-lg shadow-md p-4"
        >
          <div className="text-center">
            <div className="text-2xl mb-2">🎨</div>
            <div className="text-2xl font-bold text-purple-600">
              {stats?.totalImages || 0}
            </div>
            <div className="text-sm text-gray-600">이미지 생성</div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-lg shadow-md p-4"
        >
          <div className="text-center">
            <div className="text-2xl mb-2">🎯</div>
            <div className="text-2xl font-bold text-orange-600">
              {stats?.favoriteTopics?.length || 0}
            </div>
            <div className="text-sm text-gray-600">학습 주제</div>
          </div>
        </motion.div>
      </div>

      {/* 일별 활동 차트 */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="mb-6"
      >
        <ActivityLineChart
          data={activityData}
          title="최근 2주 활동 현황"
        />
      </motion.div>

      {/* 파이 차트들 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.6 }}
        >
          <TopicPieChart
            data={topicData}
            title="대화 주제 분포"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.7 }}
        >
          <CategoryPieChart
            data={categoryData}
            title="이미지 카테고리 분포"
          />
        </motion.div>
      </div>

      {/* 인기 주제 태그 */}
      {stats?.favoriteTopics && stats.favoriteTopics.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            자주 배운 주제
          </h3>
          <div className="flex flex-wrap gap-2">
            {stats.favoriteTopics.map((topic, index) => (
              <div
                key={index}
                className="px-4 py-2 bg-gradient-to-r from-purple-100 to-blue-100 text-purple-800 rounded-full text-sm font-medium"
              >
                {topic}
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default LearningAnalytics;
